import React, { Component } from "react";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import { TbMoon, TbSun } from "react-icons/tb";
import BrandLogo from "../../components/BrandLogo";
import HomeWrapper from "./index.style";
import { LANGUAGES, normalizeLanguage, storeLanguage } from "../../i18n";
import { THEME_MODES } from "../../theme";

const DataPreview = ({ t }) => (
  <div className="vizbox-static">
    <div className="vizbox-badges">
      <span className="vizbox-pill">{t("landing.preview.data.badges.districts")}</span>
      <span className="vizbox-pill">{t("landing.preview.data.badges.municipalities")}</span>
      <span className="vizbox-pill">{t("landing.preview.data.badges.stations")}</span>
    </div>
    <div className="vizbox-grid">
      <div className="vizbox-card">
        <span className="vizbox-label">{t("landing.preview.data.cards.coverage.label")}</span>
        <strong>{t("landing.preview.data.cards.coverage.value")}</strong>
        <p>{t("landing.preview.data.cards.coverage.body")}</p>
      </div>
      <div className="vizbox-card">
        <span className="vizbox-label">{t("landing.preview.data.cards.languages.label")}</span>
        <strong>{t("landing.preview.data.cards.languages.value")}</strong>
        <p>{t("landing.preview.data.cards.languages.body")}</p>
      </div>
      <div className="vizbox-card">
        <span className="vizbox-label">{t("landing.preview.data.cards.facts.label")}</span>
        <strong>{t("landing.preview.data.cards.facts.value")}</strong>
        <p>{t("landing.preview.data.cards.facts.body")}</p>
      </div>
      <div className="vizbox-card">
        <span className="vizbox-label">{t("landing.preview.data.cards.shape.label")}</span>
        <strong>{t("landing.preview.data.cards.shape.value")}</strong>
        <p>{t("landing.preview.data.cards.shape.body")}</p>
      </div>
    </div>
    <div className="vizbox-note">{t("landing.preview.data.note")}</div>
  </div>
);

DataPreview.propTypes = {
  t: PropTypes.func.isRequired,
};

DataPreview.defaultProps = {};

const ApiPreview = ({ t }) => (
  <div className="vizbox-static alt">
    <div className="vizbox-list">
      <div className="vizbox-list-item">
        <span>{t("landing.preview.api.rows.models.label")}</span>
        <strong>{t("landing.preview.api.rows.models.value")}</strong>
      </div>
      <div className="vizbox-list-item">
        <span>{t("landing.preview.api.rows.formats.label")}</span>
        <strong>{t("landing.preview.api.rows.formats.value")}</strong>
      </div>
      <div className="vizbox-list-item">
        <span>{t("landing.preview.api.rows.filters.label")}</span>
        <strong>{t("landing.preview.api.rows.filters.value")}</strong>
      </div>
      <div className="vizbox-list-item">
        <span>{t("landing.preview.api.rows.transport.label")}</span>
        <strong>{t("landing.preview.api.rows.transport.value")}</strong>
      </div>
    </div>
    <div className="vizbox-card wide">
      <span className="vizbox-label">{t("landing.preview.api.examplesLabel")}</span>
      <code>{t("landing.preview.api.examples.index")}</code>
      <code>{t("landing.preview.api.examples.show")}</code>
      <code>{t("landing.preview.api.examples.filter")}</code>
    </div>
  </div>
);

ApiPreview.propTypes = {
  t: PropTypes.func.isRequired,
};

ApiPreview.defaultProps = {};

class LandingPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      scrolled: false,
      form: { fname: "", lname: "", email: "", org: "", message: "" },
      formSent: false,
    };
  }

  componentDidMount() {
    window.addEventListener("scroll", this.handleScroll, { passive: true });
    this.syncDocumentState();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.i18n.language !== this.props.i18n.language) {
      this.syncDocumentState();
    }
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
  }

  handleScroll = () => {
    this.setState({ scrolled: window.scrollY > 40 });
  };

  handleLanguageToggle = () => {
    const { i18n } = this.props;
    const current = normalizeLanguage(i18n.language);
    const next = current === LANGUAGES.el ? LANGUAGES.en : LANGUAGES.el;

    storeLanguage(next);
    i18n.changeLanguage(next);
  };

  handleThemeToggle = () => {
    const { onThemeChange, themeMode } = this.props;
    onThemeChange(themeMode === THEME_MODES.dark ? THEME_MODES.light : THEME_MODES.dark);
  };

  handleFormChange = (field) => (e) => {
    const val = e.target.value;
    this.setState((s) => ({ form: { ...s.form, [field]: val } }));
  };

  handleFormSubmit = (e) => {
    e.preventDefault();
    this.setState({ formSent: true });
  };

  syncDocumentState = () => {
    const { i18n, t } = this.props;

    if (typeof document === "undefined") {
      return;
    }

    document.documentElement.lang = normalizeLanguage(i18n.language);
    document.title = t("app.title");
  };

  renderNav() {
    const { t, i18n, themeMode } = this.props;
    const { scrolled } = this.state;
    const lang = normalizeLanguage(i18n.language);
    const isDark = themeMode === THEME_MODES.dark;

    return (
      <nav className={`home-nav${scrolled ? " scrolled" : ""}`} role="navigation">
        <a className="home-nav-brand" href="#top">
          <BrandLogo themeMode={themeMode} alt={t("landing.brand")} className="home-brand-logo" />
        </a>

        <div className="home-nav-links" role="list">
          <a href="#data" role="listitem">{t("landing.nav.results")}</a>
          <a href="#data" role="listitem">{t("landing.nav.data")}</a>
          <a href="#analysis" role="listitem">{t("landing.nav.analysis")}</a>
          <a href="#pricing" role="listitem">{t("landing.nav.pricing")}</a>
          <a href="#about" role="listitem">{t("landing.nav.about")}</a>
        </div>

        <span className="home-nav-sp" />

        <div className="home-nav-controls">
          <div className="home-nav-lang" aria-label={t("topbar.language.ariaLabel")}>
            <button
              className={lang === LANGUAGES.el ? "on" : ""}
              onClick={lang === LANGUAGES.en ? this.handleLanguageToggle : undefined}
              type="button"
            >
              {t("topbar.language.greek")}
            </button>
            <button
              className={lang === LANGUAGES.en ? "on" : ""}
              onClick={lang === LANGUAGES.el ? this.handleLanguageToggle : undefined}
              type="button"
            >
              {t("topbar.language.english")}
            </button>
          </div>
          <button
            className="home-nav-theme"
            onClick={this.handleThemeToggle}
            aria-label={t("topbar.theme.ariaLabel")}
            title={isDark ? t("topbar.theme.light") : t("topbar.theme.dark")}
            type="button"
          >
            {isDark ? <TbSun size={18} /> : <TbMoon size={18} />}
          </button>
        </div>

        <a className="home-btn" href="#about">{t("landing.nav.start")}</a>
      </nav>
    );
  }

  renderHero() {
    const { t } = this.props;
    return (
      <section className="home-hero">
        <div className="home-wrap">
          <div className="home-hero-bar">
            <div className="home-hero-accent" aria-hidden="true" />
            <div>
              <h1>
                {t("landing.hero.headlineA")}{" "}
                <span className="em">{t("landing.hero.headlineB")}</span>{" "}
                {t("landing.hero.headlineC")}
              </h1>
              <p>{t("landing.hero.subhead")}</p>
              <div className="home-cta">
                <a className="home-btn" href="#data">{t("landing.hero.ctaResults")}</a>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  renderWhatBand() {
    const { t } = this.props;
    return (
      <section className="home-band">
        <div className="home-wrap">
          <div className="atlas-kicker">{t("landing.what.kicker")}</div>
          <h2>{t("landing.what.heading")}</h2>
          <p className="home-band-sub">{t("landing.what.body")}</p>
        </div>
      </section>
    );
  }

  renderCapabilities() {
    const { t } = this.props;
    return (
      <div className="home-wrap" id="data">
        <section className="home-feat">
          <div className="home-feat-txt">
            <div className="atlas-kicker">{t("landing.data.kicker")}</div>
            <h3>{t("landing.data.heading")}</h3>
            <p>{t("landing.data.body")}</p>
            <a className="home-btn ghost" href="#about">{t("landing.data.cta")}</a>
          </div>
          <div className="home-vizbox" aria-hidden="true">
            <DataPreview t={t} />
          </div>
        </section>

        <section className="home-feat rev" id="analysis">
          <div className="home-feat-txt">
            <div className="atlas-kicker">{t("landing.analysis.kicker")}</div>
            <h3>{t("landing.analysis.heading")}</h3>
            <p>{t("landing.analysis.body")}</p>
            <a className="home-btn ghost" href="#about">{t("landing.analysis.cta")}</a>
          </div>
          <div className="home-vizbox" aria-hidden="true">
            <ApiPreview t={t} />
          </div>
        </section>
      </div>
    );
  }

  renderAudiences() {
    const { t } = this.props;
    return (
      <div className="home-wrap" id="pricing">
        <div className="home-aud">
          <div className="home-aud-card">
            <div className="atlas-kicker">{t("landing.media.kicker")}</div>
            <h4>{t("landing.media.heading")}</h4>
            <p>{t("landing.media.body")}</p>
            <a href="#about">{t("landing.media.cta")}</a>
          </div>
          <div className="home-aud-card">
            <div className="atlas-kicker">{t("landing.parties.kicker")}</div>
            <h4>{t("landing.parties.heading")}</h4>
            <p>{t("landing.parties.body")}</p>
            <a href="#about">{t("landing.parties.cta")}</a>
          </div>
          <div className="home-aud-card">
            <div className="atlas-kicker">{t("landing.research.kicker")}</div>
            <h4>{t("landing.research.heading")}</h4>
            <p>{t("landing.research.body")}</p>
            <a href="#about">{t("landing.research.cta")}</a>
          </div>
        </div>
      </div>
    );
  }

  renderContact() {
    const { t } = this.props;
    const { form, formSent } = this.state;
    return (
      <section className="home-contact" id="about">
        <div className="home-wrap">
          <div className="home-contact-grid">
            <div>
              <div className="atlas-kicker">{t("landing.contact.kicker")}</div>
              <h2>{t("landing.contact.heading")}</h2>
              <p>{t("landing.contact.body")}</p>
            </div>
            {formSent ? (
              <div className="home-form-sent">{t("landing.contact.sent")}</div>
            ) : (
              <form className="home-form" onSubmit={this.handleFormSubmit} noValidate>
                <div>
                  <label htmlFor="cf-fname">{t("landing.contact.fname")}</label>
                  <input id="cf-fname" type="text" value={form.fname} onChange={this.handleFormChange("fname")} />
                </div>
                <div>
                  <label htmlFor="cf-lname">{t("landing.contact.lname")}</label>
                  <input id="cf-lname" type="text" value={form.lname} onChange={this.handleFormChange("lname")} />
                </div>
                <div>
                  <label htmlFor="cf-email">{t("landing.contact.email")}</label>
                  <input id="cf-email" type="email" value={form.email} onChange={this.handleFormChange("email")} />
                </div>
                <div>
                  <label htmlFor="cf-org">{t("landing.contact.org")}</label>
                  <input id="cf-org" type="text" value={form.org} onChange={this.handleFormChange("org")} />
                </div>
                <div className="full">
                  <label htmlFor="cf-msg">{t("landing.contact.message")}</label>
                  <textarea id="cf-msg" value={form.message} onChange={this.handleFormChange("message")} />
                </div>
                <div className="full">
                  <button className="home-btn" type="submit">{t("landing.contact.send")}</button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    );
  }

  renderFooter() {
    const { t } = this.props;
    return (
      <footer className="home-foot">
        <div className="home-wrap">
          <div className="home-foot-row">
            <a className="home-nav-brand" href="#top">
              <BrandLogo themeMode={this.props.themeMode} alt={t("landing.brand")} className="home-brand-logo" />
            </a>
            <span className="home-nav-sp" />
            <nav className="home-foot-links" aria-label={t("landing.footer.navigationLabel")}>
              <a href="#data">{t("landing.footer.results")}</a>
              <a href="#pricing">{t("landing.footer.pricing")}</a>
              <a href="#about">{t("landing.footer.about")}</a>
              <a href="#about">{t("landing.footer.api")}</a>
            </nav>
          </div>
          <div className="home-foot-cr">{t("landing.footer.copyright")}</div>
        </div>
      </footer>
    );
  }

  render() {
    return (
      <HomeWrapper themeMode={this.props.themeMode} id="top">
        {this.renderNav()}
        {this.renderHero()}
        {this.renderWhatBand()}
        {this.renderCapabilities()}
        {this.renderAudiences()}
        {this.renderContact()}
        {this.renderFooter()}
      </HomeWrapper>
    );
  }
}

LandingPage.propTypes = {
  t: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    language: PropTypes.string.isRequired,
    changeLanguage: PropTypes.func.isRequired,
  }).isRequired,
  themeMode: PropTypes.string,
  onThemeChange: PropTypes.func,
};

LandingPage.defaultProps = {
  themeMode: THEME_MODES.dark,
  onThemeChange: () => {},
};

export default withTranslation("common")(LandingPage);
